/**
 * Timezone-conversion helpers shared by admin + voter surfaces.
 *
 * Origin: Story 6.5 introduced `toIsoUtc` inline in
 * `web/src/admin/EventCreateWizardPage.tsx:214-259` for the create-event
 * wizard's datetime-local → ISO UTC conversion. Story 6.5 deferred-work
 * entry (e) (`_bmad-output/implementation-artifacts/deferred-work.md:254`)
 * named Stories 6.6 / 6.8 / 7.x as the consumers that would lift the
 * helper to a shared module.
 *
 * Story 6.8 is the second consumer (the reschedule-cutoff modal) and
 * performs the BYTE-EQUAL extraction per CSD #4 — the function source is
 * copied verbatim from EventCreateWizardPage so Story 6.5's existing
 * runtime + test coverage continues to apply unchanged. Future Stories
 * 7.x voter / dashboard countdown surfaces import from this module.
 *
 * NOTE: only `toIsoUtc` ships in Story 6.8; a complementary
 * `fromIsoUtc(iso, tz)` may land in Story 7.1 when the dashboard renders
 * the current cutoff in the admin's local TZ.
 */

/**
 * Convert a native datetime-local string `YYYY-MM-DDTHH:mm` (interpreted
 * as wall-clock in the chosen IANA `tz`) to an ISO 8601 UTC string.
 *
 * Algorithm (CSD #13 — `Intl.DateTimeFormat.formatToParts` round-trip):
 *   1. Parse the local string into Y/M/D/h/m fields.
 *   2. Construct a candidate UTC date from those fields directly.
 *   3. Compute how that UTC instant displays in the target TZ.
 *   4. The delta between "what we want shown in tz" and "what utcCandidate
 *      shows in tz" is the timezone offset; subtract it.
 */
export function toIsoUtc(local: string, tz: string): string {
  const [datePart, timePart] = local.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = (timePart ?? '00:00').split(':').map(Number);
  const utcCandidate = new Date(Date.UTC(y, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0));

  let observedY = y;
  let observedMo = mo ?? 1;
  let observedD = d ?? 1;
  let observedH = h ?? 0;
  let observedMi = mi ?? 0;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = fmt
      .formatToParts(utcCandidate)
      .reduce<Record<string, string>>((acc, p) => {
        acc[p.type] = p.value;
        return acc;
      }, {});
    observedY = Number(parts.year);
    observedMo = Number(parts.month);
    observedD = Number(parts.day);
    observedH = parts.hour === '24' ? 0 : Number(parts.hour);
    observedMi = Number(parts.minute);
  } catch {
    // tz invalid; treat input as UTC.
  }

  const observedUtc = Date.UTC(
    observedY,
    observedMo - 1,
    observedD,
    observedH,
    observedMi,
  );
  const offsetMs = observedUtc - utcCandidate.getTime();
  return new Date(utcCandidate.getTime() - offsetMs).toISOString();
}
